import { AssessmentProgram } from "@lib/ShiOneClient";

const programMapping = {
  "security-posture-review": AssessmentProgram.SecurityPostureReview,
  "security-posture-review-v2": AssessmentProgram.SecurityPostureReviewV2,
  "ai-readiness": AssessmentProgram.AiReadiness,
  "microsoft-adoption-assessment":
    AssessmentProgram.MicrosoftAdoptionAssessment,
  "dev-ops-posture-review": AssessmentProgram.DevOpsPostureReview,
  "aws-maturity-review": AssessmentProgram.AwsMaturity,
  tpx: AssessmentProgram.Tpx,
} as Record<string, AssessmentProgram | undefined>;

export const determineProgram = (programString: string) =>
  programMapping[programString] || AssessmentProgram.Unknown;

const enumToStringMap = Object.entries(programMapping).reduce(
  (acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }
    return { ...acc, [value]: key };
  },
  {} as Record<AssessmentProgram, string>,
);

export const programToString = (program: AssessmentProgram) =>
  enumToStringMap[program] || "unknown";

/**
 * Normalizes an AssessmentDto by converting backend display names to frontend enum values
 * This ensures compatibility between backend display names like "Security Posture Review v2"
 * and frontend enum values like AssessmentProgram.SecurityPostureReviewV2
 */
/**
 * Ensures a valid AssessmentProgram value for API calls
 * Returns Unknown if program is undefined, otherwise returns the program unchanged
 * Provides a centralized point for handling undefined programs and future mapping logic
 */
export const getApiEndpointProgram = (program: AssessmentProgram | undefined): AssessmentProgram => {
  if (!program) return AssessmentProgram.Unknown;

  return program;
};

export const normalizeAssessmentDto = <T extends { program?: string | AssessmentProgram }>(
  dto: T
): T => {
  if (!dto.program) return dto;

  // Map display names to enum values
  const displayNameToEnumMap: Record<string, AssessmentProgram> = {
    "Security Posture Review": AssessmentProgram.SecurityPostureReview,
    "Security Posture Review v2": AssessmentProgram.SecurityPostureReviewV2,
    "AI Readiness": AssessmentProgram.AiReadiness,
    "DevOps Posture Review": AssessmentProgram.DevOpsPostureReview,
    "AWS Maturity": AssessmentProgram.AwsMaturity,
    "Microsoft Adoption Assessment": AssessmentProgram.MicrosoftAdoptionAssessment,
    "TPX": AssessmentProgram.Tpx,
  };

  // If it's already an enum value, return as-is
  if (Object.values(AssessmentProgram).includes(dto.program as AssessmentProgram)) {
    return dto;
  }

  // Convert display name to enum value
  const enumValue = displayNameToEnumMap[dto.program as string];
  if (enumValue) {
    return { ...dto, program: enumValue };
  }

  return dto;
};
